import React, { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "./context/userContext";
import { Button, Table, Modal, Input } from "antd";
import useToken from "./customHook/token";
import Avatar from "antd/es/avatar/avatar";
import { UserOutlined, EditOutlined } from "@ant-design/icons";

const UserProfile = () => {
  const { setUserDetails, userDetails, loading, getUserDetail } =
    useContext(UserContext);
  const [editUserDetails, setEditUserDetails] = useState({
    name: "",
    email: "",
  });
  const [gameData, setGameData] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [profilePic, setProfilePic] = useState(null);

  const navigate = useNavigate();
  const { token, setToken } = useToken();

  const baseUrl = "http://localhost:5000/profilepic/";

  useEffect(() => {
    if (userDetails) {
      setEditUserDetails({
        name: userDetails.name || "",
        email: userDetails.email || "",
      });
    }
  }, [userDetails]);

  useEffect(() => {
    setToken(token);
  }, [setToken, token]);

  useEffect(() => {
    const fetchGameData = async () => {
      if (!token) {
        console.error("No token found. User might not be logged in.");
        return;
      }

      try {
        const res = await fetch("http://localhost:5000/userGameData", {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        if (res.ok) {
          const data = await res.json();
          setGameData(data);
        } else {
          console.log("Failed to fetch game data:", res.status);
        }
      } catch (error) {
        console.error("Error fetching game data:", error);
      }
    };

    fetchGameData();
  }, [token]);

  if (loading) {
    return <div>Loading...</div>;
  }

  if (!token) {
    navigate("/Sign-In");
    return null;
  }

  const columns = [
    {
      title: <div className="text-center">Date</div>,
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text) => new Date(text).toLocaleDateString(),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      defaultSortOrder: "descend",
      className: "text-center",
    },
    {
      title: <div className="text-center">Number of Rolls</div>,
      dataIndex: "noOfRolls",
      key: "noOfRolls",
      sorter: (a, b) => a.noOfRolls - b.noOfRolls,
      className: "text-center",
    },
    {
      title: <div className="text-center">Game Time</div>,
      dataIndex: "time",
      key: "time",
      sorter: (a, b) => a.time - b.time,
      className: "text-center",
    },
  ];

  const showModal = () => setIsModalOpen(true);

  const handleOk = async () => {
    try {
      const res = await fetch("http://localhost:5000/userProfile/edit", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(editUserDetails),
      });

      if (res.ok) {
        const data = await res.json();
        setUserDetails(data);
        setIsModalOpen(false);
        getUserDetail(token);
      } else {
        console.log("Failed to update user details");
      }
    } catch (error) {
      console.error("Error editing user details:", error);
    }
  };

  const handleCancel = () => setIsModalOpen(false);

  const handleEditUserDetails = (e) => {
    const { name, value } = e.target;
    setEditUserDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleProfilePictureChange = (e) => setProfilePic(e.target.files[0]);

  const handleImageUpload = async () => {
    if (!profilePic) {
      console.error("No profile picture selected");
      return;
    }

    const formData = new FormData();
    formData.append("profilePic", profilePic);

    try {
      const res = await fetch("http://localhost:5000/userProfile/edit", {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      if (res.ok) {
        const data = await res.json();
        setUserDetails(data); // Update user details with the response data
        getUserDetail(token);
      } else {
        console.error("Failed to update user details:", res.statusText);
      }
    } catch (error) {
      console.error("Error editing user details:", error);
    }
  };

  return (
    <div className="mx-auto p-4 bg-white rounded-lg shadow-md mt-8">
      <div className="text-center w-1/2 mx-auto">
        <div className="flex justify-end mb-4">
          <Button type="primary" onClick={showModal} className="text-black">
            <EditOutlined />
          </Button>
          <Modal
            title="Edit Profile"
            open={isModalOpen}
            onOk={handleOk}
            onCancel={handleCancel}
            okText="Save"
            okButtonProps={{
              disabled:
                userDetails?.name === editUserDetails?.name &&
                userDetails?.email === editUserDetails?.email,
              className:
                "py-2 px-4 bg-indigo-600 text-white font-semibold rounded-md shadow hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500",
            }}
            cancelButtonProps={{
              className:
                "py-2 px-4 border border-gray-300 text-gray-700 rounded-md shadow hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500",
            }}
          >
            <div className="text-center mb-4">
              {!userDetails || userDetails.profilePic === null ? (
                <Avatar size={64} icon={<UserOutlined />} />
              ) : (
                <img
                  src={`${baseUrl}${userDetails.profilePic}`}
                  alt="User Avatar"
                  className="w-24 h-24 rounded-full mx-auto"
                />
              )}
            </div>

            <div className="flex flex-col gap-4 mb-4">
              <label
                htmlFor="image-upload"
                className="bg-blue-500 cursor-pointer text-white py-2 px-4 rounded-md shadow hover:bg-blue-400 text-center"
              >
                Upload Image
              </label>
              <input
                type="file"
                id="image-upload"
                accept="image/*"
                onChange={handleProfilePictureChange}
                className="hidden"
              />
              <Button
                onClick={handleImageUpload}
                className="bg-indigo-600 text-white font-semibold rounded-md shadow hover:bg-indigo-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Submit Image
              </Button>
            </div>

            <form>
              <div className="mb-4">
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <Input
                  type="text"
                  id="name"
                  name="name"
                  value={editUserDetails.name}
                  onChange={handleEditUserDetails}
                  placeholder="Enter your name"
                  className="mt-1"
                />
              </div>

              <div className="mb-4">
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email
                </label>
                <Input
                  type="email"
                  id="email"
                  name="email"
                  value={editUserDetails.email}
                  onChange={handleEditUserDetails}
                  placeholder="Enter your email"
                  className="mt-1"
                />
              </div>
            </form>
          </Modal>
        </div>

        <div className="mb-4">
          {!userDetails ? (
            <Avatar size={64} icon={<UserOutlined />} />
          ) : (
            <img
              src={`${baseUrl}${userDetails.profilePic}`}
              alt="User Avatar"
              className="w-24 h-24 rounded-full mx-auto"
            />
          )}
        </div>

        <h1 className="text-xl font-semibold">{userDetails?.name}</h1>
        <h2 className="font-semibold">{userDetails?.email}</h2>
      </div>

      <div className="mt-6">
        <h2 className="text-lg font-semibold mb-2">Gameplay History</h2>
        <Table columns={columns} dataSource={gameData} rowKey="_id" />
      </div>
    </div>
  );
};

export default UserProfile;
